import type Account from "@travily/types/models/Account";
import type Message from "@travily/types/models/MessagesMessage";
import type { MessageReaction } from "@ui/components/ReactionPicker";
import type { ReactNode } from "react";
import { ConversationHeader } from "@/components/ConversationHeader/ConversationHeader";
import { MessageComposer } from "@/components/MessageComposer/MessageComposer";
import { MessageList } from "@/components/MessageList/MessageList";
import type { UploadFile } from "@/hooks/use-direct-upload-multiple";

export interface ConversationDetailViewProps {
  account: Account;
  currentUserId: number;
  isSending: boolean;
  messageValue: string;
  messages: Message[] | undefined;
  onMessageChange: (value: string) => void;
  onAddFiles: (files: FileList) => void;
  onRemoveFile: (id: string) => void;
  onSendMessage: () => void;
  onTyping?: () => void;
  attachments: UploadFile[];
  onToggleReaction: (messageId: number, emoji: string) => void;
  reactions: Record<string, MessageReaction[]>;
  /** When the other side last opened the thread. Null until they have. */
  readAt: string | null;
  /** Names for the read receipt. Empty when the counterpart has no display name to show. */
  readers: string[];
  /** The counterpart's typing bubble, shown at the foot of the thread. */
  typingIndicator?: ReactNode;
}

export function ConversationDetailView({
  account,
  currentUserId,
  isSending,
  messageValue,
  messages,
  onMessageChange,
  onAddFiles,
  onRemoveFile,
  onSendMessage,
  onTyping,
  attachments,
  onToggleReaction,
  reactions,
  readAt,
  readers,
  typingIndicator,
}: ConversationDetailViewProps) {
  return (
    // `overflow-hidden` gives this box an automatic minimum size of zero, which is what lets the
    // thread own the only scroll: without it the message list's intrinsic height would push the
    // composer past the viewport bottom through every flex ancestor up to Frame.
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="hidden px-6 pt-4 md:block">
        <ConversationHeader account={account} />
      </div>
      <MessageList
        currentUserId={currentUserId}
        messages={messages}
        onToggleReaction={onToggleReaction}
        reactions={reactions}
        readAt={readAt}
        readers={readers}
        typingIndicator={typingIndicator}
      />
      <div className="px-4 pb-safe-[0.75rem] md:px-6 md:pb-5">
        <MessageComposer
          attachments={attachments}
          isSending={isSending}
          messageValue={messageValue}
          onAddFiles={onAddFiles}
          onMessageChange={onMessageChange}
          onRemoveFile={onRemoveFile}
          onSendMessage={onSendMessage}
          onTyping={onTyping}
        />
      </div>
    </div>
  );
}
